import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import {
  U2000IdColumnDef,
  type U2000TableColumn,
} from '../../../u2000Core/components/tables/basic/U2000BasicTableList';
import { U2000IdCheckedBoxDisplay } from '../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay';
import { U2000IdHeaderCheckedBoxDisplay } from '../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay';
import { u2000BuildAppRouteHref } from '../../../u2000Core/tools/u2000OpenAppRouteInNewTab';
import { NS, u3146FormatCurrency } from '../../U3146';
import {
  u3146OpenStandardInvoiceBalanceDue,
  u3146PreviewLineCustomerDisplayName,
  u3146PreviewLineIsEligible,
  u3146PreviewLineShowsTecColumns,
  u3146PreviewReasonLabelKey,
  type U3146OpenStandardInvoice,
  type U3146PreviewLine,
  type U3146PreviewNotEligibleReason,
} from '../../U3146Service';

export type U3146PreviewGridRow = {
  id: string;
  idChecked?: boolean;
  eligible: boolean;
  projectId: string;
  projetNumber: string;
  projetName: string;
  customerName: string;
  ineligibilityReason?: U3146PreviewNotEligibleReason;
  createdAt?: string | null;
  lastBillableTecDate?: string;
  daysSinceLastBillableTec?: number;
  uninvoicedTecLineCount?: number;
  uninvoicedTecAmountTec?: number;
  willCreateWriteOff?: boolean;
  openStandardInvoices?: U3146OpenStandardInvoice[];
  showsTecColumns?: boolean;
};

export function u3146PreviewLineToGridRow(line: U3146PreviewLine, idChecked: boolean): U3146PreviewGridRow {
  const eligible = u3146PreviewLineIsEligible(line);
  const showsTecColumns = u3146PreviewLineShowsTecColumns(line);
  const tecFromEligible = eligible && line.status === 'eligible' ? line : null;
  const tecFromRecent =
    !eligible && line.status === 'not_eligible' && line.reason === 'recent_billable_tec' ? line : null;
  return {
    id: line.projectId,
    idChecked: eligible ? idChecked : false,
    eligible,
    projectId: line.projectId,
    projetNumber: line.projetNumber,
    projetName: line.projetName,
    customerName: u3146PreviewLineCustomerDisplayName(line),
    ineligibilityReason: eligible ? undefined : line.reason,
    createdAt: line.createdAt,
    showsTecColumns,
    lastBillableTecDate: showsTecColumns
      ? (tecFromEligible?.lastBillableTecDate ?? tecFromRecent?.lastBillableTecDate)
      : undefined,
    daysSinceLastBillableTec: showsTecColumns
      ? (tecFromEligible?.daysSinceLastBillableTec ?? tecFromRecent?.daysSinceLastBillableTec)
      : undefined,
    uninvoicedTecLineCount: showsTecColumns
      ? (tecFromEligible?.uninvoicedTecLineCount ?? tecFromRecent?.uninvoicedTecLineCount)
      : undefined,
    uninvoicedTecAmountTec: showsTecColumns
      ? (tecFromEligible?.uninvoicedTecAmountTec ?? tecFromRecent?.uninvoicedTecAmountTec)
      : undefined,
    willCreateWriteOff: eligible ? line.willCreateWriteOff : undefined,
    openStandardInvoices: eligible ? line.openStandardInvoices : undefined,
  };
}

type U3146PreviewGridColumnOptions = {
  readonly: boolean;
  hasEligibleRows: boolean;
};

function gridHeaderLabel(fullLabel: string) {
  return (_column: U2000TableColumn, label: string) => (
    <span
      className="block text-xs font-medium leading-tight text-gray-900"
      title={label !== fullLabel ? fullLabel : undefined}
    >
      {label}
    </span>
  );
}

function truncateCell(value: ReactNode) {
  if (value == null || value === '') return '—';
  const text = String(value);
  return (
    <span className="block min-w-0 truncate" title={text}>
      {text}
    </span>
  );
}

export function u3146InitCloseInactivePreviewGridColumns(
  t: TFunction,
  opts: U3146PreviewGridColumnOptions,
): U2000TableColumn[] {
  const { readonly, hasEligibleRows } = opts;
  const headerDisabled = readonly || !hasEligibleRows;

  return [
    {
      ...U2000IdColumnDef,
      headerDisplay: (column, label, allRowSelected, handleSelectAllRow) => (
        <U2000IdHeaderCheckedBoxDisplay
          column={column}
          label={label}
          disabled={headerDisabled}
          allRowSelected={allRowSelected}
          handleSelectAllRow={handleSelectAllRow}
        />
      ),
      display: (column, value, row: U3146PreviewGridRow, allowRowSelection, rowUpdated) => {
        if (!row.eligible) {
          return <span className="block h-6" aria-hidden />;
        }
        return (
          <U2000IdCheckedBoxDisplay
            column={column}
            value={value}
            row={row}
            disabled={readonly}
            rowUpdated={rowUpdated}
            allowRowSelection={allowRowSelection && !readonly}
          />
        );
      },
    },
    {
      name: 'projetNumber',
      label: t('lblProjectNumber', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      widthClass: 'w-20 whitespace-nowrap',
      noTruncate: true,
      headerDisplay: gridHeaderLabel(t('lblProjectNumber', { ns: NS })),
    },
    {
      name: 'projetName',
      label: t('lblProjectName', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      widthClass: 'w-[15%]',
      headerDisplay: gridHeaderLabel(t('lblProjectName', { ns: NS })),
      display: (_column, value) => truncateCell(value),
    },
    {
      name: 'customerName',
      label: t('lblCustomer', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      widthClass: 'w-[13%]',
      headerDisplay: gridHeaderLabel(t('lblCustomer', { ns: NS })),
      display: (_column, value) => truncateCell(value),
    },
    {
      name: 'eligibleStatus',
      label: t('lblEligible', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'center',
      widthClass: 'w-18 whitespace-nowrap',
      noTruncate: true,
      headerDisplay: gridHeaderLabel(t('lblEligible', { ns: NS })),
      display: (_column, _value, row: U3146PreviewGridRow) =>
        row.eligible ? (
          <span className="font-medium text-green-700">{t('lblYes', { ns: NS })}</span>
        ) : (
          <span className="text-gray-500">{t('lblNo', { ns: NS })}</span>
        ),
    },
    {
      name: 'ineligibilityReason',
      label: t('lblIneligibilityReason', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      widthClass: 'w-[12%]',
      headerDisplay: gridHeaderLabel(t('lblIneligibilityReason', { ns: NS })),
      display: (_column, _value, row: U3146PreviewGridRow) => {
        if (row.eligible || !row.ineligibilityReason) return '—';
        const text = t(u3146PreviewReasonLabelKey(row.ineligibilityReason), { ns: NS });
        return truncateCell(text);
      },
    },
    {
      name: 'createdAt',
      label: t('lblGridProjectCreatedAt', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'date',
      align: 'left',
      widthClass: 'w-27 whitespace-nowrap',
      noTruncate: true,
      headerDisplay: gridHeaderLabel(t('lblProjectCreatedAt', { ns: NS })),
    },
    {
      name: 'lastBillableTecDate',
      label: t('lblGridLastBillableTecDate', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'date',
      align: 'left',
      widthClass: 'w-27 whitespace-nowrap',
      noTruncate: true,
      headerDisplay: gridHeaderLabel(t('lblLastBillableTecDate', { ns: NS })),
      display: (_column, value, row: U3146PreviewGridRow) => (row.showsTecColumns ? value : '—'),
    },
    {
      name: 'daysSinceLastBillableTec',
      label: t('lblGridDaysSinceLastBillableTec', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'number',
      align: 'right',
      widthClass: 'w-16 whitespace-nowrap',
      noTruncate: true,
      headerDisplay: gridHeaderLabel(t('lblDaysSinceLastBillableTec', { ns: NS })),
      display: (_column, value, row: U3146PreviewGridRow) => (row.showsTecColumns ? value : '—'),
    },
    {
      name: 'uninvoicedTecAmountTec',
      label: t('lblGridUninvoicedTec', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      defaultSortOrder: 'ASC',
      sortable: true,
      type: 'number',
      align: 'right',
      widthClass: 'w-40',
      headerDisplay: gridHeaderLabel(t('lblUninvoicedTec', { ns: NS })),
      display: (_column, _value, row: U3146PreviewGridRow) => {
        if (!row.showsTecColumns) return '—';
        const text = t('wizardUninvoicedTecSummary', {
          ns: NS,
          count: row.uninvoicedTecLineCount ?? 0,
          amount: u3146FormatCurrency(row.uninvoicedTecAmountTec ?? 0),
        });
        return truncateCell(text);
      },
    },
    {
      name: 'willCreateWriteOff',
      label: t('lblWillCreateWriteOff', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'center',
      widthClass: 'w-18 whitespace-nowrap',
      noTruncate: true,
      headerDisplay: gridHeaderLabel(t('lblWillCreateWriteOff', { ns: NS })),
      display: (_column, _value, row: U3146PreviewGridRow) =>
        row.eligible ? (row.willCreateWriteOff ? t('lblYes', { ns: NS }) : t('lblNo', { ns: NS })) : '—',
    },
    {
      name: 'openStandardInvoices',
      label: t('lblGridOpenStandardInvoices', { ns: NS }),
      inGridField: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      widthClass: 'w-[16%]',
      headerDisplay: gridHeaderLabel(t('lblOpenStandardInvoices', { ns: NS })),
      display: (_column, _value, row: U3146PreviewGridRow) => {
        if (!row.eligible || !row.openStandardInvoices?.length) {
          return '—';
        }
        return (
          <ul className="m-0 min-w-0 list-none space-y-0.5 p-0 text-xs leading-snug">
            {row.openStandardInvoices.map((inv) => {
              const showBalance = inv.status === 'RELEASE';
              const label = inv.invoiceDisplayName || inv.invoiceNumber;
              const balanceText = showBalance
                ? t('wizardInvoiceBalanceDue', {
                    ns: NS,
                    amount: u3146FormatCurrency(u3146OpenStandardInvoiceBalanceDue(inv)),
                  })
                : null;
              const lineText = balanceText ? `${label} — ${balanceText}` : label;
              return (
                <li key={inv.invoiceId} className="min-w-0 truncate" title={lineText}>
                  <a
                    href={u2000BuildAppRouteHref(`/U3125/${inv.invoiceId}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {label}
                  </a>
                  {balanceText ? <span className="text-gray-700"> — {balanceText}</span> : null}
                </li>
              );
            })}
          </ul>
        );
      },
    },
  ];
}
